import { formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, FormBuilder, FormArray } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { UnitConversionService } from 'src/app/shared/services/unit-conversion.service';

@Component({
  selector: 'app-project-detail',
  templateUrl: './project-detail.component.html',
  styleUrl: './project-detail.component.css'
})
export class ProjectDetailComponent implements OnInit {
  projectId: any;
  projectName = "";
  userD: any;
  organizationId: any;
  branchList: any;
  branchShow = false;
  accessMode: 'owner' | 'awarded' | string = 'owner';

  get isAwardedAccess(): boolean {
    return this.accessMode === 'awarded';
  }
  
  form: FormGroup = new FormGroup({
    project_name: new FormControl(""),
    project_id: new FormControl(""),
    company_name: new FormControl(""),
    company_code: new FormControl(""),
    company_id: new FormControl(""),
    branch_id: new FormControl(""),
    branch_name: new FormControl(""),
    domain: new FormControl(""),
    sub_domain: new FormControl(""),
    infrastructure_type: new FormControl(""),
    road_type: new FormControl(""),
    road_services: new FormControl([]),
    size: new FormControl(""),
    client_name: new FormControl(""),
    start_date: new FormControl(""),
    end_date: new FormControl(""),
    budget_amount: new FormControl(""),
    funding_source: new FormControl(""),
    scope_description: new FormControl(""),
    timeline_start: new FormControl(""),
    timeline_end: new FormControl(""),
    permit_status: new FormControl(""),
    stage: new FormControl(""),
    stage_document: new FormControl(""),
    fsi: new FormControl<number>(2),
  });

  selectedDomain = '';
  selectedDoc = '';
  selectedDom = '';
  domainOptions: any = [];

  // Sub-domain mapping based on domain
  subDomainMapping: { [key: string]: string[] } = {
    'construction': ['Residential', 'Commercial', 'Education'],
    'consultancy': ['ESG Consultancy', 'Architects', 'Other'],
    'road': ['National Highway', 'State Highway', 'District Roads', 'Village Road'],
    'utilities': ['Water', 'Gas', 'Electricity']
  };

  // Road services mapping based on road type
  roadServicesMapping: { [key: string]: string[] } = {
    'National Highway': [
      'New Road',
      'Tree cover',
      'Parking bays',
      'Toll Plaza construction',
      'Footpath construction',
      'Amenities Construction',
      'Shopping Mall Construction',
      'New Drainage',
      'Maintenance - Road',
      'Maintenance - Parking bays',
      'Maintenance - Tree',
      'Maintenance - Toll Plaza',
      'Maintenance - Amenities',
      'Maintenance - shopping Mall',
      'Maintenance - Drainage'
    ],
    'State Highway': [
      'New Road',
      'Tree cover',
      'Parking bays',
      'Toll Plaza construction',
      'Footpath construction',
      'Amenities Construction',
      'Shopping Mall Construction',
      'New Drainage',
      'Maintenance - Road',
      'Maintenance - Parking bays',
      'Maintenance - Tree',
      'Maintenance - Toll Plaza',
      'Maintenance - Amenities',
      'Maintenance - shopping Mall',
      'Maintenance - Drainage'
    ],
    'District Roads': [
      'New Road',
      'Tree cover',
      'Parking bays',
      'Toll Plaza construction',
      'Footpath construction',
      'Amenities Construction',
      'Shopping Mall Construction',
      'New Drainage',
      'Maintenance - Road',
      'Maintenance - Parking bays',
      'Maintenance - Tree',
      'Maintenance - Toll Plaza',
      'Maintenance - Amenities',
      'Maintenance - shopping Mall',
      'Maintenance - Drainage'
    ],
    'Village Road': [
      'New Road',
      'Tree cover',
      'Parking bays',
      'Toll Plaza construction',
      'Footpath construction',
      'Amenities Construction',
      'Shopping Mall Construction',
      'New Drainage',
      'Maintenance - Road',
      'Maintenance - Parking bays',
      'Maintenance - Tree',
      'Maintenance - Toll Plaza',
      'Maintenance - Amenities',
      'Maintenance - shopping Mall',
      'Maintenance - Drainage'
    ]
  };

  // Get sub-domains for selected domain
  getSubDomains(): string[] {
    const domain = this.form?.get('domain')?.value?.toLowerCase();
    return this.subDomainMapping[domain] || [];
  }

  // Get road services for selected road type
  getRoadServices(): string[] {
    const roadType = this.form?.get('sub_domain')?.value;
    if (this.form?.get('domain')?.value?.toLowerCase() === 'road' && roadType) {
      return this.roadServicesMapping[roadType] || [];
    }
    return [];
  }

  // Check if road services section should be displayed
  showRoadServices(): boolean {
    return this.form?.get('domain')?.value?.toLowerCase() === 'road' && 
           this.form?.get('sub_domain')?.value;
  }

  // Check if a service is selected
  isServiceSelected(service: string): boolean {
    const services = this.form.get('road_services')?.value || [];
    return services.includes(service);
  }

  domainFields: { [key: string]: any[] } = {
    Residential: [
      { key: 'housing_units', label: 'Housing Units', type: 'number', value: '' },
      { key: 'unit_estimate', label: 'Unit Estimate', type: 'number', value: '' },
      { key: 'unit_layout_changes', label: 'Unit Layout Changes', type: 'text', value: '' },
      { key: 'foundation_progress', label: 'Foundation Progress', type: 'percentage', value: '' },
      { key: 'unit_completion_rate', label: 'Unit Completion Rate', type: 'number', value: '' },
      { key: 'fire_safety_compliance', label: 'Fire Safety Compliance', type: 'text', value: '' },
      { key: 'occupancy_certificate', label: 'Occupancy Certificate', type: 'text', value: '' },
      { key: 'handover_date', label: 'Handover Date', type: 'date', value: '' },
      { key: 'resident_onboarding_docs', label: 'Resident Onboarding Docs', type: 'text', value: '' },
      { key: 'tile_type_issued', label: 'Tile Type Issued', type: 'text', value: '' },
      { key: 'paint_quantity', label: 'Paint Quantity', type: 'volume', value: '' },
      { key: 'tile_pallets_received', label: 'Tile Pallets Received', type: 'number', value: '' },
      { key: 'paint_drums', label: 'Paint Drums', type: 'volume', value: '' },
    ],
    Commercial: [
      { key: 'retail_space', label: 'Retail Space', type: 'size', value: '' },
      { key: 'market_demand_analysis', label: 'Market Demand Analysis', type: 'text', value: '' },
      { key: 'storefront_design', label: 'Storefront Design', type: 'text', value: '' },
      { key: 'store_construction', label: 'Store Construction', type: 'text', value: '' },
      { key: 'hvac_performance_logs', label: 'HVAC Performance Logs', type: 'text', value: '' },
      { key: 'elevator_downtime', label: 'Elevator Downtime', type: 'duration', value: '' },
      { key: 'lease_agreement_finalized', label: 'Lease Agreement Finalized', type: 'text', value: '' },
      { key: 'facility_management_plan', label: 'Facility Management Plan', type: 'text', value: '' },
      { key: 'hvac_ducts_issued', label: 'HVAC Ducts Issued', type: 'number', value: '' },
      { key: 'fire_safety_equipment', label: 'Fire Safety Equipment', type: 'text', value: '' },
      { key: 'hvac_units_received', label: 'HVAC Units Received', type: 'number', value: '' },
      { key: 'fire_safety_gear', label: 'Fire Safety Gear', type: 'text', value: '' },
    ],
    Educational: [
      { key: 'enrollment_projection', label: 'Enrollment Projection', type: 'number', value: '' },
      { key: 'curriculum_planning', label: 'Curriculum Planning', type: 'text', value: '' },
      { key: 'classroom_adjustments', label: 'Classroom Adjustments', type: 'number', value: '' },
      { key: 'building_erection', label: 'Building Erection', type: 'percentage', value: '' },
      { key: 'student_safety_index', label: 'Student Safety Index', type: 'number', value: '' },
      { key: 'accreditation_status', label: 'Accreditation Status', type: 'text', value: '' },
      { key: 'staff_induction_schedule', label: 'Staff Induction Schedule', type: 'date', value: '' },
      { key: 'student_admission_start', label: 'Student Admission Start', type: 'date', value: '' },
      { key: 'lab_furniture_issued', label: 'Lab Furniture Issued', type: 'number', value: '' },
      { key: 'sports_gear', label: 'Sports Gear', type: 'text', value: '' },
      { key: 'lab_equipment_boxes', label: 'Lab Equipment Boxes', type: 'number', value: '' },
      { key: 'sports_equipment', label: 'Sports Equipment', type: 'text', value: '' },
    ],
    Water: [
      { key: 'water_demand', label: 'Water Demand', type: 'volume', value: '' },
      { key: 'pipeline_route', label: 'Pipeline Route', type: 'distance', value: '' },
      { key: 'flow_rate_adjustment', label: 'Flow Rate Adjustment', type: 'volume', value: '' },
      { key: 'pipe_installation', label: 'Pipe Installation', type: 'distance', value: '' },
      { key: 'flow_rate_consistency', label: 'Flow Rate Consistency', type: 'volume', value: '' },
      { key: 'water_quality_clearance', label: 'Water Quality Clearance', type: 'text', value: '' },
      { key: 'operational_sops', label: 'Operational SOPs', type: 'text', value: '' },
      { key: 'pipe_sections_issued', label: 'Pipe Sections Issued', type: 'number', value: '' },
      { key: 'valve_units', label: 'Valve Units', type: 'number', value: '' },
      { key: 'pipe_sections', label: 'Pipe Sections', type: 'number', value: '' },
      { key: 'chlorination_units', label: 'Chlorination Units', type: 'number', value: '' },
    ],
    Power: [
      { key: 'power_demand', label: 'Power Demand', type: 'power', value: '' },
      { key: 'substation_location', label: 'Substation Location', type: 'coordinates', value: '' },
      { key: 'load_balancing', label: 'Load Balancing', type: 'power', value: '' },
      { key: 'cable_laying', label: 'Cable Laying', type: 'distance', value: '' },
      { key: 'voltage_stability_logs', label: 'Voltage Stability Logs', type: 'text', value: '' },
      { key: 'grid_integration_status', label: 'Grid Integration Status', type: 'text', value: '' },
      { key: 'transformer_oil', label: 'Transformer Oil', type: 'volume', value: '' },
      { key: 'cable_rolls', label: 'Cable Rolls', type: 'number', value: '' },
      { key: 'cable_drums', label: 'Cable Drums', type: 'number', value: '' },
      { key: 'transformer_oil_drums', label: 'Transformer Oil Drums', type: 'volume', value: '' },
    ],
    Gas: [
      { key: 'gas_demand', label: 'Gas Demand', type: 'volume', value: '' },
      { key: 'pressure_requirements', label: 'Pressure Requirements', type: 'pressure', value: '' },
      { key: 'pressure_adjustment', label: 'Pressure Adjustment', type: 'pressure', value: '' },
      { key: 'valve_installation', label: 'Valve Installation', type: 'number', value: '' },
      { key: 'leak_detection_alerts', label: 'Leak Detection Alerts', type: 'number', value: '' },
      { key: 'safety_clearance', label: 'Safety Clearance', type: 'text', value: '' },
      { key: 'welding_rods', label: 'Welding Rods', type: 'number', value: '' },
      { key: 'pressure_gauges', label: 'Pressure Gauges', type: 'number', value: '' },
      { key: 'pressure_regulators', label: 'Pressure Regulators', type: 'number', value: '' },
      { key: 'safety_valves', label: 'Safety Valves', type: 'number', value: '' },
    ],
    Telecom: [
      { key: 'coverage_estimate', label: 'Coverage Estimate', type: 'area', value: '' },
      { key: 'tower_locations', label: 'Tower Locations', type: 'number', value: '' },
      { key: 'bandwidth_upgrade', label: 'Bandwidth Upgrade', type: 'bandwidth', value: '' },
      { key: 'antenna_setup', label: 'Antenna Setup', type: 'number', value: '' },
      { key: 'signal_strength_logs', label: 'Signal Strength Logs', type: 'text', value: '' },
      { key: 'equipment_health_index', label: 'Equipment Health Index', type: 'percentage', value: '' },
      { key: 'network_activation_date', label: 'Network Activation Date', type: 'date', value: '' },
      { key: 'sla_documentation', label: 'SLA Documentation', type: 'text', value: '' },
      { key: 'customer_onboarding_kit', label: 'Customer Onboarding Kit', type: 'text', value: '' },
      { key: 'fiber_cable_spools', label: 'Fiber Cable Spools', type: 'number', value: '' },
      { key: 'tower_bolts', label: 'Tower Bolts', type: 'number', value: '' },
      { key: 'antenna_units', label: 'Antenna Units', type: 'number', value: '' },
    ],
    Road: [
      { key: 'traffic_volume', label: 'Traffic Volume', type: 'number', value: '' },
      { key: 'lane_configuration', label: 'Lane Configuration', type: 'number', value: '' },
      { key: 'lane_width_adjustment', label: 'Lane Width Adjustment', type: 'distance', value: '' },
      { key: 'road_length', label: 'Road length', type: 'distance', value: '' },
      { key: 'pavement_layering', label: 'Pavement Layering', type: 'text', value: '' },
      { key: 'surface_condition_index', label: 'Surface Condition Index', type: 'number', value: '' },
      { key: 'road_safety_audit', label: 'Road Safety Audit', type: 'text', value: '' },
      { key: 'traffic_signage_installed', label: 'Traffic Signage Installed', type: 'text', value: '' },
      { key: 'public_access_date', label: 'Public Access Date', type: 'date', value: '' },
      { key: 'asphalt_quantity', label: 'Asphalt Quantity', type: 'weight', value: '' },
      { key: 'road_marking_paint', label: 'Road Marking Paint', type: 'volume', value: '' },
      { key: 'asphalt_delivered', label: 'Asphalt Delivered', type: 'weight', value: '' },
      { key: 'road_signage_boards', label: 'Road Signage Boards', type: 'number', value: '' },
    ],
    Consultancy: [
      { key: 'consultancy_scope', label: 'Consultancy Scope', type: 'text', value: '' },
      { key: 'resource_allocation', label: 'Resource Allocation', type: 'text', value: '' },
      { key: 'scope_change_request', label: 'Scope Change Request', type: 'text', value: '' },
      { key: 'oversight_report', label: 'Oversight Report', type: 'text', value: '' },
      { key: 'monitoring_review_notes', label: 'Monitoring Review Notes', type: 'text', value: '' },
      { key: 'client_satisfaction_score', label: 'Client Satisfaction Score', type: 'percentage', value: '' },
      { key: 'risk_mitigation_updates', label: 'Risk Mitigation Updates', type: 'text', value: '' },
      { key: 'final_advisory_report', label: 'Final Advisory Report', type: 'text', value: '' },
      { key: 'client_feedback_summary', label: 'Client Feedback Summary', type: 'text', value: '' },
      { key: 'knowledge_transfer_docs', label: 'Knowledge Transfer Docs', type: 'text', value: '' },
      { key: 'stationery_issued', label: 'Stationery Issued', type: 'text', value: '' },
      { key: 'field_survey_kits', label: 'Field Survey Kits', type: 'number', value: '' },
      { key: 'survey_equipment', label: 'Survey Equipment', type: 'text', value: '' },
      { key: 'reference_materials', label: 'Reference Materials', type: 'text', value: '' },
    ]
  };

  permitStatusOptions = ['Approved', 'Pending'];
  stages: any[] = [{id: 1, name: 'Pre-Planning'}, {id: 2, name: 'Planning'}, {id: 3, name: 'Survey'}, {id: 4, name: 'Design Revisions & Scope Adjustments'}, {id: 5, name: 'Execution'}, {id: 6, name: 'Closure'}];
  stageDocsList = ["feasibility","sanction","completion"];

  // FSI options for construction projects (0.5 to 4.0 with 0.5 increments)
  fsiOptions: number[] = [];

  readonly defaultFsiBySubDomain: Record<string, number> = {
    Residential: 2,
    Commercial: 2.5,
    Education: 2,
  };

  compareFsi = (a: number | string | null | undefined, b: number | string | null | undefined): boolean => {
    const na = a === null || a === undefined || a === '' ? NaN : Number(a);
    const nb = b === null || b === undefined || b === '' ? NaN : Number(b);
    if (Number.isNaN(na) || Number.isNaN(nb)) return false;
    return Math.abs(na - nb) < 1e-9;
  };

  normalizeFsiForSelect(raw: unknown, subDomainHint?: string | null): number {
    const fallback =
      (subDomainHint && this.defaultFsiBySubDomain[subDomainHint.trim()]) ?? 2;
    if (raw === null || raw === undefined || raw === '') {
      return fallback;
    }
    const n = typeof raw === 'number' ? raw : parseFloat(String(raw).trim());
    if (Number.isNaN(n) || n <= 0) {
      return fallback;
    }
    const allowed = this.fsiOptions;
    if (allowed.length === 0) {
      return Math.min(4, Math.max(0.5, Math.round(n * 2) / 2));
    }
    if (allowed.some((v) => Math.abs(v - n) < 1e-9)) {
      return allowed.find((v) => Math.abs(v - n) < 1e-9)!;
    }
    const stepped = Math.round(n * 2) / 2;
    const clamped = Math.min(4, Math.max(0.5, stepped));
    if (allowed.some((v) => Math.abs(v - clamped) < 1e-9)) {
      return clamped;
    }
    return allowed.reduce((best, v) =>
      Math.abs(v - n) < Math.abs(best - n) ? v : best
    );
  }

  // Unit conversion tracking
  fieldUnits: { [key: string]: string } = {};
  sizeInSqm: number | null = null;

  constructor(
    private fb: FormBuilder,
    private formBuilder: FormBuilder,
    private apiService: ApiService,
    private toastrMsgService: ToastrMsgService,
    private router: Router,
    private route: ActivatedRoute,
    private unitConversionService: UnitConversionService
  ) {}

  ngOnInit(): void {
    const user = localStorage.getItem('user') as any;
    this.userD = JSON.parse(user);
    console.log(this.userD);
    this.organizationId = this.userD.user.org_id;
    this.domainOptions = this.userD.user?.domain.split(",") || [];
    
    // Generate FSI options from 0.5 to 4.0 with 0.5 increments
    this.fsiOptions = [];
    for (let i = 0.5; i <= 4.0; i += 0.5) {
      this.fsiOptions.push(parseFloat(i.toFixed(1)));
    }
    
    this.route.params.subscribe((params) => {
      this.projectId = params['id'];
      if (this.projectId) {
        this.getProjectDetails();
      }
    });
    
    this.fetchBranchList();
  }

  fetchBranchList() {
    this.apiService.getOrganizationsBranch(this.organizationId).subscribe({
      next: (res: any) => {
        if (res?.data?.length != 0) {
          this.branchList = res?.data;
          this.branchShow = true;
        }
      },
      error: (err) => {
        this.toastrMsgService.error(err.error.message);
      },
      complete: () => '',
    });
  }

  getProjectDetails() {
    this.apiService.getProjectCommonId(this.projectId).subscribe({
      next: (res: any) => {
        this.projectName = res.project_name;
        this.projectId = res.id;
        this.accessMode = res.access_mode || 'owner';

        // Set selected domain for domain fields display
        this.selectedDomain = res.domain || '';

        this.form.patchValue({
          project_name: res.project_name,
          project_id: res.id,
          company_name: this.isAwardedAccess ? (res.company_name || res.parent_org_name || '') : this.userD.user.org_name,
          company_id: this.isAwardedAccess ? res.org_id : this.userD.user.org_id,
          company_code: this.isAwardedAccess ? (res.company_code || '') : this.userD.user.company_code,
          branch_id: +res.branch_id,
          branch_name: res.branch_name,
          domain: res.domain,
          sub_domain: res.sub_domain || '',
          infrastructure_type: res.infrastructure_type,
          size: res.size,
          client_name: res.client_name,
          start_date: res.start_date ? formatDate(res.start_date, "yyyy-MM-dd", "en-US") : "",
          end_date: res.end_date ? formatDate(res.end_date, "yyyy-MM-dd", "en-US") : "",
          budget_amount: res.budget_amount,
          funding_source: res.funding_source,
          scope_description: res.scope_description,
          timeline_start: res.timeline_start ? formatDate(res.timeline_start, "yyyy-MM-dd", "en-US") : "",
          timeline_end: res.timeline_end ? formatDate(res.timeline_end, "yyyy-MM-dd", "en-US") : "",
          permit_status: res.permit_status,
          stage: res.stage,
          fsi: this.normalizeFsiForSelect(res.fsi, res.sub_domain),
        });

        // Set size unit if available
        if (res.size_unit) {
          this.fieldUnits['size'] = res.size_unit;
        } else if (res.size) {
          this.fieldUnits['size'] = 'sqm';
        }

        // Load road_type and road_services from metadata if domain is road
        if (res.domain && res.domain.toLowerCase() === 'road' && res.metadata) {
          try {
            const metadata = typeof res.metadata === 'string' ? JSON.parse(res.metadata) : res.metadata;
            if (metadata.road_services && Array.isArray(metadata.road_services)) {
              this.form.patchValue({ road_services: metadata.road_services });
            }
          } catch (e) {
            console.error('Error parsing metadata for road project:', e);
          }
        }

        // Add domain-specific fields to form
        if (this.selectedDomain && this.domainFields[this.selectedDomain]) {
          this.domainFields[this.selectedDomain].forEach(field => {
            if (!this.form.get(field.key)) {
              this.form.addControl(field.key, new FormControl(''));
            }
            if (res.hasOwnProperty(field.key)) {
              this.form.get(field.key)?.patchValue(res[field.key]);
            }
            // Set unit if available
            if (res[`${field.key}_unit`]) {
              this.fieldUnits[field.key] = res[`${field.key}_unit`];
            }
          });
        }

        // Update size in SQM for display
        this.updateSizeInSqm();
      },
      error: (err) => {
        console.log(err);
        this.toastrMsgService.error(err.error?.errorMessage || err.error?.error || 'Failed to load project details');
      },
      complete: () => console.log("Observable complete"),
    });
  }

  /**
   * Update size in SQM for display
   */
  updateSizeInSqm() {
    if (!this.form || !this.unitConversionService) {
      this.sizeInSqm = null;
      return;
    }

    const sizeValue = this.form.get('size')?.value;
    if (!sizeValue || isNaN(parseFloat(sizeValue))) {
      this.sizeInSqm = null;
      return;
    }

    const currentUnit = this.getFieldUnit('size');
    const numericValue = parseFloat(sizeValue);
    
    // If already in sqm or m2, use as is
    if (currentUnit === 'sqm' || currentUnit === 'm2') {
      this.sizeInSqm = numericValue;
      return;
    }

    // Convert to sqm using the service
    try {
      const converted = this.unitConversionService.convert(numericValue, currentUnit, 'sqm');
      this.sizeInSqm = converted !== null ? converted : numericValue;
    } catch (error) {
      console.warn('Error converting size to sqm:', error);
      this.sizeInSqm = numericValue;
    }
  }

  /**
   * Get size in SQM for display (readonly)
   */
  getSizeInSqm(): number | null {
    return this.sizeInSqm;
  }

  /**
   * Get current unit for a field
   */
  getFieldUnit(fieldKey: string): string {
    if (this.fieldUnits[fieldKey]) {
      return this.fieldUnits[fieldKey];
    }
    // Default units
    if (fieldKey === 'size') {
      return 'sqm';
    }
    return 'nos';
  }

  /**
   * Get available units for a field type
   */
  getAvailableUnitsForField(fieldType: string): any[] {
    if (!fieldType || !this.unitConversionService) {
      return [];
    }
    
    try {
      const units = this.unitConversionService.getUnitsForFieldType(fieldType);
      if (units && Array.isArray(units) && units.length > 0) {
        return units.map(u => ({ code: u.code, name: u.name }));
      }
    } catch (error) {
      console.error('Error getting available units:', error);
    }
    
    return [];
  }

  /**
   * Resolve the display name for the currently bound branch_id. Falls back
   * to whatever was stored on `branch_name` in the form, then to "—".
   */
  getBranchName(): string {
    const id = this.form?.get('branch_id')?.value;
    if (id !== null && id !== undefined && id !== '') {
      const match = (this.branchList || []).find(
        (b: any) => Number(b?.branch_id) === Number(id)
      );
      if (match?.branch_name) return match.branch_name;
    }
    return this.form?.get('branch_name')?.value || '—';
  }

  /**
   * Generic empty-value placeholder helper used by the read-only details view.
   */
  displayValue(value: any): string {
    if (value === null || value === undefined) return '—';
    const s = String(value).trim();
    return s ? s : '—';
  }

  /**
   * Check if document checklist should be shown
   * Show for Construction, Road, and domains with regulatory requirements
   */
  showDocumentChecklist(): boolean {
    const domain = this.form?.get('domain')?.value?.toLowerCase();
    const domainsWithChecklist = ['construction', 'road', 'utilities'];
    return domainsWithChecklist.includes(domain);
  }

  /**
   * Handle document updated event from checklist component
   */
  onDocumentUpdated(event: any): void {
    console.log('Document updated:', event);
    // Optionally refresh project details or show notification
  }
}
